import { RbacModule, RbacAclRule, RbacRole, RBAC_SUBMODULES } from './rbac.models';
import { SUBMODULE_SEED_ROLES, MODULE_ROUTE_ROLES } from './role-sets';

/**
 * #1106 — Réconcilier une matrice `rbac_acl` déjà seedée avec le seed corrigé.
 *
 * LE PROBLÈME QUE ÇA RÉSOUT
 *
 * `RbacService.seedLegacyAclRules()` saute tout module ayant déjà au moins une
 * règle (`modulesWithRules`). C'est voulu : on ne veut jamais écraser une
 * matrice affinée à la main. Mais du coup, une CORRECTION du seed (#1099,
 * #1026, #1103) n'atteint jamais un environnement déjà seedé. La réconciliation
 * de dev a dû être faite à la main, en REST, le 2026-08-04.
 *
 * POURQUOI CE CALCUL VIT ICI ET PAS DANS LE SERVICE
 *
 * C'est une fonction pure : ni Firestore, ni Angular, ni réseau. Elle est donc
 * testable directement, contrairement à tout ce qui vit dans `RbacService`,
 * dont le spec exclut explicitement les écritures Firestore de son périmètre.
 * C'est précisément pour ça que `seedLegacyAclRules` n'a jamais eu de test.
 *
 * ON DÉSACTIVE, ON NE SUPPRIME JAMAIS
 *
 * `isActive: false` est réversible, et déjà respecté par les deux chargeurs :
 * `rbac.service.ts` côté client, `rbac-claims.ts` côté serveur. Un `delete`
 * serait irréversible et ferait perdre l'historique.
 */

/** Une cellule de la matrice telle que le seed l'attend. */
export interface ExpectedAclCell {
  module: RbacModule;
  subModule: string;
  roleName: string;
}

export interface AclReconciliationPlan {
  /** Présentes dans le seed attendu, absentes de la matrice. */
  toCreate: ExpectedAclCell[];
  /** Actives en base, hors du seed attendu : à désactiver, jamais à supprimer. */
  toDeactivate: RbacAclRule[];
  /** Dans le seed attendu mais désactivées : à réactiver. */
  toReactivate: RbacAclRule[];
  /** Conformes, rien à faire. */
  unchanged: number;
  /**
   * Règles dont le `roleId` ne résout aucun rôle connu, ou doublons du même
   * triplet. Jamais touchées : on ne sait pas ce qu'elles portent.
   */
  orphans: RbacAclRule[];
  /**
   * Modules qui se retrouveraient SANS AUCUNE règle active après application.
   *
   * Ce n'est pas cosmétique : `coveredModules` est calculé sur les règles
   * actives, et les rules Firestore lisent le même `rbacCov`. Un module qui
   * sort de la matrice retombe sur les droits legacy de la route — un
   * élargissement silencieux des droits, pas un blocage. À montrer avant
   * d'appliquer quoi que ce soit.
   */
  modulesLeftEmpty: RbacModule[];
}

/**
 * Une opération que la CF n'a PAS appliquée, et pourquoi — miroir de
 * `SkippedOp` (functions/src/functions/admin/reconcile-acl-matrix.ts).
 *
 * Affiché tel quel : un « 3 ignorées » silencieux ferait croire à une
 * réconciliation complète alors qu'elle ne l'est pas.
 */
export interface AclReconciliationSkip {
  kind: 'create' | 'deactivate' | 'reactivate';
  ref: string;
  reason: 'not_found' | 'already_in_target_state' | 'duplicate' | 'unknown_role';
}

/** Compte-rendu de la CF `reconcileAclMatrix`. */
export interface AclReconciliationResult {
  created: number;
  deactivated: number;
  reactivated: number;
  skipped: AclReconciliationSkip[];
  dryRun: boolean;
}

/** Clé naturelle d'une cellule. `rbac_acl` n'en a pas : ses IDs sont auto-générés. */
function cellKey(module: string, subModule: string, roleName: string): string {
  return `${module} ${subModule} ${roleName}`;
}

/**
 * Le seed attendu, calculé sans Firestore — même logique que
 * `seedLegacyAclRules`, moins la garde par module.
 */
export function computeExpectedAclCells(): ExpectedAclCell[] {
  const cells: ExpectedAclCell[] = [];
  for (const [module, subModules] of Object.entries(RBAC_SUBMODULES)) {
    // `admin` est exclu du seed, exactement comme dans seedLegacyAclRules.
    if (module === 'admin') continue;
    for (const sub of subModules) {
      const roleNames = SUBMODULE_SEED_ROLES[sub.key]
        ?? MODULE_ROUTE_ROLES[module as RbacModule] ?? [];
      for (const roleName of roleNames) {
        cells.push({ module: module as RbacModule, subModule: sub.key, roleName });
      }
    }
  }
  return cells;
}

/**
 * Diff pur entre la matrice réelle et le seed attendu. Aucune écriture.
 *
 * `roleId` est ambigu par construction : le seed écrit un identifiant résolu
 * depuis `rbac_roles`, mais l'évaluation runtime accepte aussi un `roleId` qui
 * EST un nom de rôle. On résout donc les deux formes — sinon le plan
 * proposerait de désactiver des règles parfaitement légitimes.
 */
export function computeAclReconciliationPlan(
  existing: RbacAclRule[],
  roles: RbacRole[],
  opts?: { modules?: RbacModule[] },
): AclReconciliationPlan {
  const nameById = new Map(roles.map(r => [r.id, r.name]));
  const knownNames = new Set(roles.map(r => r.name));
  const resolveRoleName = (roleId: string): string | null =>
    nameById.get(roleId) ?? (knownNames.has(roleId) ? roleId : null);

  const scope = opts?.modules ? new Set(opts.modules) : null;
  const inScope = (m: RbacModule) => !scope || scope.has(m);

  const expected = computeExpectedAclCells().filter(c => inScope(c.module));
  const expectedByKey = new Map(
    expected.map(c => [cellKey(c.module, c.subModule, c.roleName), c]),
  );

  const plan: AclReconciliationPlan = {
    toCreate: [], toDeactivate: [], toReactivate: [],
    unchanged: 0, orphans: [], modulesLeftEmpty: [],
  };

  const seenKeys = new Set<string>();
  const activeAfterByModule = new Map<RbacModule, number>();
  const bump = (m: RbacModule) =>
    activeAfterByModule.set(m, (activeAfterByModule.get(m) ?? 0) + 1);

  for (const rule of existing) {
    if (!inScope(rule.module)) {
      if (rule.isActive) bump(rule.module);
      continue;
    }
    const roleName = resolveRoleName(rule.roleId);
    if (!roleName) {
      plan.orphans.push(rule);
      if (rule.isActive) bump(rule.module);
      continue;
    }

    const key = cellKey(rule.module, rule.subModule, roleName);
    if (seenKeys.has(key)) {
      // Doublon du même triplet : `rbac_acl` n'a pas de clé naturelle, rien
      // n'empêche deux documents identiques. On n'en touche aucun.
      plan.orphans.push(rule);
      if (rule.isActive) bump(rule.module);
      continue;
    }
    seenKeys.add(key);

    if (expectedByKey.has(key)) {
      if (rule.isActive) {
        plan.unchanged++;
        bump(rule.module);
      } else {
        plan.toReactivate.push(rule);
        bump(rule.module);
      }
    } else if (rule.isActive) {
      plan.toDeactivate.push(rule);
    } else {
      plan.unchanged++;
    }
  }

  for (const [key, cell] of expectedByKey) {
    if (!seenKeys.has(key)) {
      plan.toCreate.push(cell);
      bump(cell.module);
    }
  }

  const touched = new Set<RbacModule>([
    ...existing.filter(r => inScope(r.module)).map(r => r.module),
    ...expected.map(c => c.module),
  ]);
  for (const m of touched) {
    if ((activeAfterByModule.get(m) ?? 0) === 0) plan.modulesLeftEmpty.push(m);
  }

  return plan;
}

/** Le plan a-t-il quoi que ce soit à appliquer ? */
export function isReconciliationPlanEmpty(plan: AclReconciliationPlan): boolean {
  return plan.toCreate.length === 0
    && plan.toDeactivate.length === 0
    && plan.toReactivate.length === 0;
}
